<template>
  <div class="studio-panel h-full">
    <!-- Panel Header -->
    <div class="panel-header" v-if="!inDrawer">
      <span>{{ $t('Data Source') }}</span>
      <el-button v-if="isMobile" circle text @click="$emit('close-mobile')">
        <el-icon><Close /></el-icon>
      </el-button>
    </div>

    <div class="panel-content custom-scrollbar">
      
      <!-- CTA -->
      <div v-if="true" class="mb-4 p-3 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg border border-indigo-100">
         <div class="flex items-center justify-between pointer-events-none mb-2">
            <span class="text-xs font-bold text-indigo-800 uppercase">Premium</span>
            <el-icon class="text-indigo-400"><PriceTag /></el-icon>
         </div>
         <el-button 
            type="primary" 
            size="small"
            class="w-full"
            @click="$emit('price-click')"
         >
            {{ $t('Unlock Features') }}
         </el-button>
      </div>

      <!-- Quick Analyze -->
      <div class="panel-section">
        <div class="panel-section-title">
          <el-icon class="mr-1"><EditPen /></el-icon> {{ $t('Quick Paste') }}
        </div>
        <el-input 
          :model-value="textToAnalyze"
          :placeholder="$t('Paste text to analyze...')"
          type="textarea"
          :rows="3"
          resize="none"
          class="mb-2"
          @input="$emit('update:textToAnalyze', $event)"
        ></el-input>
        <el-button 
          plain 
          size="small" 
          class="w-full" 
          @click="$emit('analyze-text')"
          :loading="loading"
        >
          {{ $t('Analyze') }}
        </el-button>
      </div>

      <!-- Manual Input -->
      <div class="panel-section">
        <div class="panel-section-title">
           <el-icon class="mr-1"><Plus /></el-icon> {{ $t('Manual Entry') }}
        </div>
        <div class="flex gap-2 mb-2">
           <el-input v-model="inputKey" :placeholder="$t('Word')" size="small" />
           <el-input v-model="inputValue" type="number" :placeholder="$t('Val')" size="small" style="width: 80px;" />
        </div>
        <el-button type="primary" plain size="small" class="w-full" @click="addLocalData">
           {{ $t('Add Entry') }}
        </el-button>
      </div>

      <!-- Data List -->
      <div class="panel-section flex-1">
         <div class="panel-section-title flex justify-between">
           <span>{{ $t('Dataset') }} ({{ processedChartData.length }})</span>
           <el-button link type="danger" size="small" class="!h-auto !p-0 text-xs" @click="$emit('clear-data')">{{ $t('Clear') }}</el-button>
         </div>
         
         <div class="bg-gray-50 rounded border border-gray-100 overflow-hidden min-h-[200px] flex flex-col">
            <template v-if="processedChartData.length > 0">
              <el-table
                :data="processedChartData" 
                :key="tableKey"
                class="w-full !bg-transparent"
                :show-header="false"
                size="small"
                height="200"
              >
                <el-table-column :label="$t('Name')">
                  <template #default="{ row, $index }">
                    <el-input 
                      v-model="row.name" 
                      size="small" 
                      @change="handleDataChange($index, 'name', row.name)"
                      class="!border-0 !shadow-none hover:!border-gray-200 focus-within:!border-indigo-500"
                    />
                  </template>
                </el-table-column>
                <el-table-column :label="$t('Value')" width="110" align="right">
                  <template #default="{ row, $index }">
                    <el-input-number 
                      v-model="row.value" 
                      size="small"
                      :min="1"
                      controls-position="right"
                      @change="handleDataChange($index, 'value', row.value)"
                      class="!w-full"
                    />
                  </template>
                </el-table-column>
                <el-table-column width="45" align="center">
                  <template #default="scope">
                    <el-button 
                      link 
                      type="danger" 
                      size="small"
                      @click.stop="$emit('delete-data', scope.$index)"
                      class="!p-1"
                    >
                      <el-icon><Delete /></el-icon>
                    </el-button>
                  </template>
                </el-table-column>              
              </el-table>
            </template>
            <div v-else class="flex flex-col items-center justify-center flex-1 p-4 text-center">
               <el-icon class="text-gray-300 text-3xl mb-2"><FolderOpened /></el-icon>
               <span class="text-xs text-gray-400">{{ $t('No data. Add words locally or paste text above.') }}</span>
            </div>
         </div>
      </div>
    
      <!-- Footer -->
      <div class="mt-4 pt-4 border-t border-gray-100 text-center">
         <el-select 
            :model-value="$i18n.locale" 
            @change="(val) => $emit('change-lang', val)" 
            size="small" 
            class="w-full mb-2"
          >
           <el-option value="en" label="English" />
           <el-option value="zh-CN" label="简体中文" />
           <el-option value="de" label="Deutsch" />
           <el-option value="es" label="Español" />
           <el-option value="fr" label="Français" />
           <el-option value="it" label="Italiano" />
           <el-option value="ja" label="日本語" />
           <el-option value="ko" label="한국어" />
           <el-option value="pt" label="Português" />
         </el-select>
         <div class="flex justify-center gap-3 text-xs text-gray-400">
           <span class="cursor-pointer hover:text-indigo-500" @click="$emit('open-service')">{{ $t('Support') }}</span>
           <a href="./blog/" target="_blank" class="hover:text-indigo-500">{{ $t('Blog') }}</a>
         </div>
      </div>

    </div>
  </div>
</template>

<script>
import { ElButton, ElIcon, ElInput, ElInputNumber, ElTable, ElTableColumn, ElSelect, ElOption } from 'element-plus';
import { Close, Delete, PriceTag, Plus, EditPen, FolderOpened } from '@element-plus/icons-vue';

export default {
  name: 'LeftSidebar',
  components: { ElButton, ElIcon, ElInput, ElInputNumber, ElTable, ElTableColumn, ElSelect, ElOption, Close, Delete, PriceTag, Plus, EditPen, FolderOpened },
  props: {
    isMobile: Boolean,
    textToAnalyze: String,
    processedChartData: Array,
    loading: Boolean,
    tableKey: Number,
    inDrawer: Boolean
  },
  emits: ['update:textToAnalyze', 'analyze-text', 'add-data', 'delete-data', 'update-data', 'change-lang', 'price-click', 'open-service', 'close-mobile', 'clear-data'],
  data() {
    return {
      inputKey: '',
      inputValue: ''
    };
  },
  methods: {
    addLocalData() {
      if (this.inputKey && this.inputValue) {
        this.$emit('add-data', { key: this.inputKey, value: this.inputValue });
        this.inputKey = '';
        this.inputValue = '';
      }
    },
    handleDataChange(index, field, value) {
      this.$emit('update-data', { index, field, value });
    }
  }
}
</script>
